import { motion, useInView, useScroll, useTransform, AnimatePresence } from "framer-motion";
import { useRef, useState, useEffect } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import { Users, Eye, Heart } from "lucide-react";

const valueKeys = [
  { icon: Users, titleKey: "value.teamwork.title", descKey: "value.teamwork.desc" },
  { icon: Eye, titleKey: "value.transparency.title", descKey: "value.transparency.desc" },
  { icon: Heart, titleKey: "value.commitment.title", descKey: "value.commitment.desc" },
];
export { valueKeys as values };

const rotatingWordsEs = ["estrategia", "datos", "creatividad", "growth", "resultados"];
const rotatingWordsEn = ["strategy", "data", "creativity", "growth", "results"];
const wordColors = [
  "hsl(var(--brand-rosa))",
  "hsl(var(--brand-amarillo))",
  "hsl(var(--brand-uva))",
  "hsl(var(--brand-rosa))",
  "hsl(var(--brand-amarillo))",
];

const orbWordsEs = ["Growth", "Ecosistema", "IA Aplicada", "Estrategia", "Hub Digital", "Full Funnel", "Metodología", "Paid Media", "Datos", "Ejecución"];
const orbWordsEn = ["Growth", "Ecosystem", "Applied AI", "Strategy", "Digital Hub", "Full Funnel", "Methodology", "Paid Media", "Data", "Execution"];
const orbColors = [
  "hsl(var(--brand-rosa))",
  "hsl(var(--brand-amarillo))",
  "hsl(var(--brand-uva))",
  "hsl(var(--brand-rosa))",
  "hsl(var(--brand-amarillo))",
  "hsl(var(--brand-uva))",
  "hsl(var(--brand-rosa))",
  "hsl(var(--brand-amarillo))",
  "hsl(var(--brand-uva))",
  "hsl(var(--brand-rosa))",
];

const GlowOrb = ({ inView, wordIdx, scrollProgress, lang }: { inView: boolean; wordIdx: number; scrollProgress: any; lang: string }) => {
  const orbY = useTransform(scrollProgress, [0, 1], [-24, 48]);
  const orbScale = useTransform(scrollProgress, [0, 0.3, 0.7, 1], [0.92, 1.02, 1.02, 0.95]);
  const orbRotate = useTransform(scrollProgress, [0, 1], [0, 15]);
  const counterRotate = useTransform(scrollProgress, [0, 1], [0, -15]);

  return (
    <motion.div
      className="relative flex h-full w-full items-center justify-center overflow-visible"
      style={{ y: orbY, scale: orbScale, rotate: orbRotate }}
    >
      <div className="relative aspect-square w-[min(82vw,360px)] md:w-[min(62vw,440px)] lg:w-[min(36vw,520px)]">
        <motion.div
          className="absolute inset-[-4%] rounded-full"
          animate={{
            scale: [1, 1.08, 1],
            opacity: [0.2, 0.32, 0.2],
          }}
          transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
          style={{
            background: `radial-gradient(circle, ${orbColors[wordIdx]}33 0%, transparent 70%)`,
          }}
        />

        <motion.div
          className="relative h-full w-full rounded-full"
          initial={{ scale: 0, opacity: 0 }}
          animate={inView ? {
            scale: 1,
            opacity: 1,
            rotate: [0, 360],
          } : {}}
          transition={{
            scale: { duration: 1, ease: [0.22, 1, 0.36, 1] },
            opacity: { duration: 0.8 },
            rotate: { duration: 4, repeat: Infinity, ease: "linear" },
          }}
        >
          <div
            className="absolute inset-0 rounded-full"
            style={{
              background: `conic-gradient(from 0deg, hsl(var(--brand-rosa)), hsl(var(--brand-amarillo)), #0D0D0D, hsl(var(--brand-uva)), hsl(var(--brand-rosa)))`,
            }}
          />
          <div
            className="absolute inset-[12%] rounded-full bg-white"
            style={{
              boxShadow: `0 0 80px 30px ${orbColors[wordIdx]}26 inset`,
            }}
          />
        </motion.div>

        <motion.div
          className="absolute inset-0 flex items-center justify-center pointer-events-none"
          style={{ rotate: counterRotate }}
        >
          <AnimatePresence mode="wait">
            <motion.span
              key={wordIdx}
              className="text-2xl md:text-3xl lg:text-4xl font-black tracking-tight text-foreground"
              initial={{ opacity: 0, y: 10, filter: "blur(12px)" }}
              animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
              exit={{ opacity: 0, y: -10, filter: "blur(12px)" }}
              transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            >
              {(lang === "en" ? orbWordsEn : orbWordsEs)[wordIdx]}
            </motion.span>
          </AnimatePresence>
        </motion.div>
      </div>
    </motion.div>
  );
};

const AboutSection = () => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });
  const { t, lang } = useLanguage();
  const orbWords = lang === "en" ? orbWordsEn : orbWordsEs;
  const [wordIdx, setWordIdx] = useState(0);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const textY = useTransform(scrollYProgress, [0, 1], [100, -80]);
  const sectionScale = useTransform(scrollYProgress, [0, 0.15, 0.85, 1], [0.92, 1, 1, 0.94]);
  const sectionOpacity = useTransform(scrollYProgress, [0, 0.08, 0.92, 1], [0, 1, 1, 0.3]);

  useEffect(() => {
    if (!inView) return;
    const interval = setInterval(() => {
      setWordIdx((prev) => (prev + 1) % orbWords.length);
    }, 2000);
    return () => clearInterval(interval);
  }, [inView]);

  return (
    <section
      id="nosotros"
      className="relative bg-background text-foreground overflow-hidden"
      ref={ref}
    >
      <motion.div className="grid lg:grid-cols-[1fr_0.8fr] lg:min-h-[700px]" style={{ scale: sectionScale, opacity: sectionOpacity }}>
        <motion.div
          className="px-6 md:px-12 lg:px-16 py-16 md:py-20 lg:py-44 flex flex-col justify-center relative z-10"
          style={{ y: textY }}
        >
          <motion.h2
            className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-black tracking-[-0.03em] leading-[0.95] lowercase mb-8"
            initial={{ opacity: 0, y: 60, filter: "blur(12px)" }}
            animate={inView ? { opacity: 1, y: 0, filter: "blur(0px)" } : {}}
            transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
          >
            {t("about.headline1")}
            <br />
            <motion.span
              className="text-foreground/20"
              initial={{ opacity: 0 }}
              animate={inView ? { opacity: 1 } : {}}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              {t("about.headline2")}
            </motion.span>
            <br />
            <motion.span
              className="text-foreground/20"
              initial={{ opacity: 0 }}
              animate={inView ? { opacity: 1 } : {}}
              transition={{ duration: 0.6, delay: 0.7 }}
            >
              {t("about.headline3")}
            </motion.span>{" "}
            <motion.span
              className="text-brand-rosa inline-block"
              initial={{ scale: 0, rotate: 10 }}
              animate={inView ? { scale: 1, rotate: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.9, type: "spring", stiffness: 200 }}
            >
              {t("about.headline4")}
            </motion.span>
          </motion.h2>

          <motion.p
            className="text-base md:text-lg text-muted-foreground/60 max-w-lg leading-relaxed mb-8"
            initial={{ opacity: 0, y: 30, filter: "blur(8px)" }}
            animate={inView ? { opacity: 1, y: 0, filter: "blur(0px)" } : {}}
            transition={{ duration: 0.8, delay: 1.0 }}
          >
            {t("about.desc")}
          </motion.p>
        </motion.div>

        <motion.div
          className="relative flex w-full items-center justify-center h-[360px] md:h-[500px] lg:h-auto pb-10 md:pb-14 lg:pb-0"
          initial={{ opacity: 0 }}
          animate={inView ? { opacity: 1 } : {}}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          <GlowOrb inView={inView} wordIdx={wordIdx} scrollProgress={scrollYProgress} lang={lang} />
        </motion.div>
      </motion.div>
    </section>
  );
};

export default AboutSection;
